import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";

import { useUser } from "@/hooks/useUserProvider";
import { request } from "@/lib/request";

export type ActiveSession = {
  id: string;
  client: "web" | "extension-chrome" | "extension-firefox" | "legacy" | "oauth";
  oauthClientId?: string;
  browser: string | null;
  os: string | null;
  deviceType: "desktop" | "mobile" | "tablet" | null;
  ipAddress: string | null;
  loginMethod: string;
  createdAt: string;
  lastUsedAt: string;
  expiresAt: string;
  current: boolean;
};

export type SecurityEvent = {
  id: string;
  type: string;
  browser: string | null;
  os: string | null;
  client: string | null;
  ipAddress: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  currentSession: boolean;
};

// Keeps the API's error code (REAUTH_REQUIRED, INVALID_PASSWORD, ...) so the
// UI can react to it instead of only showing a message.
export class SecurityRequestError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
  ) {
    super(message);
  }
}

const rethrow =
  (fallback: string) =>
  (err: unknown): never => {
    if (isAxiosError(err)) {
      const data = err.response?.data as
        { message?: string | string[]; code?: string } | undefined;
      const message = Array.isArray(data?.message)
        ? data?.message.join(" ")
        : data?.message;
      throw new SecurityRequestError(message || fallback, data?.code);
    }
    throw new SecurityRequestError(fallback);
  };

export const sessionsQueryOptions = () =>
  queryOptions({
    queryKey: ["auth", "sessions"],
    queryFn: async (): Promise<ActiveSession[]> => {
      const res = await request
        .get("/auth/sessions")
        .catch(rethrow("Failed to load your sessions."));
      return res.data.data;
    },
  });

export const securityEventsQueryOptions = () =>
  queryOptions({
    queryKey: ["auth", "security-events"],
    queryFn: async (): Promise<SecurityEvent[]> => {
      const res = await request
        .get("/auth/security-events", { params: { limit: 12 } })
        .catch(rethrow("Failed to load recent activity."));
      return res.data.data;
    },
  });

export const useSessions = () => {
  const { accessToken } = useUser();
  return useQuery({ ...sessionsQueryOptions(), enabled: !!accessToken });
};

export const useSecurityEvents = () => {
  const { accessToken } = useUser();
  return useQuery({ ...securityEventsQueryOptions(), enabled: !!accessToken });
};

const useInvalidateSecurity = () => {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["auth"] }),
      queryClient.invalidateQueries({ queryKey: ["user"] }),
    ]);
};

export const useRevokeSession = () => {
  const invalidate = useInvalidateSecurity();
  return useMutation({
    mutationKey: ["auth", "sessions", "revoke"],
    mutationFn: async (sessionId: string) => {
      await request
        .delete(`/auth/sessions/${sessionId}`)
        .catch(rethrow("Could not sign out that session."));
    },
    meta: { analytics: { successEvent: "security_session_revoked" } },
    onSettled: invalidate,
  });
};

export const useRevokeOtherSessions = () => {
  const invalidate = useInvalidateSecurity();
  return useMutation({
    mutationKey: ["auth", "sessions", "revoke-others"],
    mutationFn: async (): Promise<number> => {
      const res = await request
        .post("/auth/sessions/revoke-others")
        .catch(rethrow("Could not sign out your other sessions."));
      return res.data.data.revoked;
    },
    meta: { analytics: { successEvent: "security_sessions_revoked" } },
    onSettled: invalidate,
  });
};

export const useReauthenticate = () =>
  useMutation({
    mutationKey: ["auth", "reauth"],
    mutationFn: async (password: string) => {
      await request
        .post("/auth/reauth", { password })
        .catch(rethrow("That password is not correct."));
    },
  });

export const useChangePassword = () => {
  const invalidate = useInvalidateSecurity();
  return useMutation({
    mutationKey: ["auth", "password", "change"],
    mutationFn: async (values: {
      currentPassword: string;
      newPassword: string;
    }) => {
      await request
        .post("/auth/password", values)
        .catch(rethrow("Could not change your password."));
    },
    meta: { analytics: { successEvent: "security_password_changed" } },
    onSuccess: invalidate,
  });
};

export const useSetPassword = () => {
  const invalidate = useInvalidateSecurity();
  return useMutation({
    mutationKey: ["auth", "password", "set"],
    mutationFn: async (password: string) => {
      await request
        .post("/auth/password/set", { password })
        .catch(rethrow("Could not set your password."));
    },
    meta: { analytics: { successEvent: "security_password_set" } },
    onSuccess: invalidate,
  });
};

export const useSendPasswordResetEmail = () =>
  useMutation({
    mutationKey: ["auth", "password", "email"],
    mutationFn: async () => {
      await request
        .post("/auth/change-password")
        .catch(rethrow("Could not send the email. Please try again."));
    },
  });

export const useSendVerificationEmail = () =>
  useMutation({
    mutationKey: ["auth", "verify-email", "request"],
    mutationFn: async () => {
      await request
        .post("/auth/request-verification-email")
        .catch(rethrow("Could not send the email. Please try again."));
    },
  });
